import { useState } from "react"
import { errorToast, toast } from "@/v2-ui/toast"
import cn from "@/v2-ui/utils/utils.cn"
import ErrorMessage from "@/v2-ui/ErrorMessage"
import Button from "@/v2-backoffice-client/components/button"
import FeatureNotice from "@/v2-backoffice-client/feature/FeatureNotice"
import FeatureCountryOption from "@/v2-backoffice-client/feature/country/FeatureCountryOption"
import FeatureCountrySkeleton from "@/v2-backoffice-client/feature/country/FeatureCountrySkeleton"
import { useFeatureCountryQuery } from "@/v2-backoffice-client/feature/country/feature.country.query"
import { useFeatureCountryMutation } from "@/v2-backoffice-client/feature/country/feature.country.mutation"
import {
  COUNTRY_EMPTY_MEANING,
  FEATURE_COUNTRY_QUERY_NAME,
  type CountryEmptyMeaning,
} from "@/v2-backoffice-client/feature/country/feature.country.constants"
import { useCountryQuery } from "@/v2-backoffice-client/country/country.query"

export type FeatureCountrySectionProps = {
  className?: string
  featureCode: string
  /** Names the thing in the copy, e.g. "basemap". */
  subject: string
  /** Surface segment of this section's feature codes, e.g. "Basemaps". */
  featureCodeLabel: string
  /**
   * What an EMPTY list means for this feature — the console reads map layers
   * and basemaps with "no country = hidden everywhere", every other feature
   * with "no country = offered everywhere". Decides the copy and whether an
   * empty list is flagged as the outage it is.
   */
  emptyMeans: CountryEmptyMeaning
  /**
   * Further operation names to refetch after a save, for surfaces that
   * derive something from the country list (the layer sheet's visibility
   * summary).
   */
  refetchQueryNames?: string[]
}

/**
 * Where a catalog feature is offered. Keyed by feature code alone, so the
 * same section serves basemaps, map layers, nav layers and anything else
 * that lands in common.FeaturesCountries.
 *
 * The empty state is the load-bearing detail, and it means opposite things
 * for different features: a generic feature with NO countries is available
 * in EVERY country, a map layer or basemap with NO countries is available
 * NOWHERE (the console inner-joins the table on the viewer's country). Both
 * look like an empty checkbox list, so the copy says which one applies
 * before the operator can misread it — an empty list on a layer is the
 * single most common reason a published layer is missing from the app.
 */
function FeatureCountrySection(props: FeatureCountrySectionProps) {
  const {
    featureCode,
    subject,
    featureCodeLabel,
    emptyMeans,
    refetchQueryNames = [],
    className,
  } = props

  const { data: countryData, loading: isLoadingCountries } = useCountryQuery()
  const {
    data,
    loading: isLoadingSelection,
    error,
  } = useFeatureCountryQuery({
    variables: { featureCode },
  })

  const countries = countryData?.getCountries ?? []
  const featureCountries = data?.getFeatureCountries

  // The saved set, re-read whenever the query lands or the sheet switches
  // feature; `draft` holds the operator's unsaved edit on top of it.
  const saved = featureCountries
    ?.filter((country) => !country.IsExcluded)
    .map((country) => country.CountryCode)
  const excluded = new Set(
    featureCountries
      ?.filter((country) => country.IsExcluded)
      .map((country) => country.CountryCode),
  )

  const [draft, setDraft] = useState<ReadonlySet<number> | null>(null)
  const [savedKey, setSavedKey] = useState<string | null>(null)
  const currentKey = saved ? `${featureCode}:${saved.join(",")}` : null
  if (currentKey !== savedKey) {
    setSavedKey(currentKey)
    setDraft(null)
  }

  const selected = draft ?? new Set(saved ?? [])

  const [save, { loading: isSaving, error: saveError }] =
    useFeatureCountryMutation({
      // The returned rows carry no cache identity of their own, so the
      // cached query result is what has to be replaced — awaited, or the
      // list would flash the pre-save set as the draft is dropped.
      refetchQueries: [FEATURE_COUNTRY_QUERY_NAME, ...refetchQueryNames],
      awaitRefetchQueries: true,
      onCompleted() {
        setDraft(null)
        toast({ title: "Country availability saved." })
      },
    })
  errorToast(saveError, { title: "Failed to save country availability" })

  if (isLoadingSelection || isLoadingCountries) {
    return <FeatureCountrySkeleton className={className} />
  }
  if (error) return <ErrorMessage className={className} error={error} />

  function toggle(code: number, isChecked: boolean) {
    const next = new Set(selected)
    if (isChecked) next.add(code)
    else next.delete(code)
    setDraft(next)
  }

  function selectAll() {
    setDraft(
      new Set(
        countries
          .map((country) => country.Code)
          .filter((code) => !excluded.has(code)),
      ),
    )
  }

  const isEmpty = selected.size === 0
  const isGated = emptyMeans === COUNTRY_EMPTY_MEANING.nowhere
  const isHiddenEverywhere = isGated && isEmpty
  const isAllSelected = countries.every(
    (country) => selected.has(country.Code) || excluded.has(country.Code),
  )

  return (
    <div className={cn("flex flex-col gap-3", className)}>
      {isHiddenEverywhere && (
        <FeatureNotice tone="destructive">
          <strong>Hidden from every customer.</strong> A {subject} with no
          country is offered nowhere — it is missing from the app in every
          country, whatever its published state says. Tick the countries it
          should appear in, or select all, and save.
        </FeatureNotice>
      )}

      <div className="flex items-start gap-3">
        <p className="flex-1 text-xs text-muted-foreground">
          {isGated ? (
            isEmpty ? (
              <>
                No country selected. Customers see this {subject} in the ticked
                countries only.
              </>
            ) : (
              <>
                Offered in the ticked countries only. Untick them all and this{" "}
                {subject} disappears from every country.
              </>
            )
          ) : isEmpty ? (
            <>
              Offered in <strong>every country</strong>. Tick countries to
              restrict it to them.
            </>
          ) : (
            <>
              Offered in the ticked countries only. Clear them all to offer this{" "}
              {subject} everywhere.
            </>
          )}
        </p>
        {isGated && (
          <Button
            size="sm"
            variant="outline"
            disabled={isSaving || isAllSelected}
            data-feature-code={`UI_Backoffice${featureCodeLabel}_Countries_SelectAll`}
            onClick={selectAll}
          >
            Select all
          </Button>
        )}
        <Button
          size="sm"
          isLoading={isSaving}
          disabled={draft === null}
          data-feature-code={`UI_Backoffice${featureCodeLabel}_Countries_Save`}
          onClick={() => {
            void save({
              variables: {
                input: {
                  FeatureCode: featureCode,
                  CountryCodes: [...selected],
                },
              },
            })
          }}
        >
          Save
        </Button>
      </div>

      <div className="flex max-w-md flex-col gap-1.5">
        {countries.map((country) => (
          <FeatureCountryOption
            key={country.Code}
            code={country.Code}
            alpha2={country.Alpha2}
            name={country.Name}
            isChecked={selected.has(country.Code)}
            isExcluded={excluded.has(country.Code)}
            disabled={isSaving}
            onCheckedChange={(isChecked) => toggle(country.Code, isChecked)}
          />
        ))}
      </div>
    </div>
  )
}

export default FeatureCountrySection
